import {
  CardContent,
  Grid,
  ListItem,
  ListItemText,
  Typography,
  useEventCallback,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { PmiCard } from 'shell/components/shared/card';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import PmiTitle from 'shell/components/shared/title';
import {
  AssignOperatorPractice,
  MetricAuthorizations,
  PracticeAuthorizations,
  PracticeResearchAuthorizations,
  ResearchRegisterAuthorizations,
  ResponsibleAuthorizations,
  TypeOperationAuthorizations,
  TypePracticeAuthorizations,
} from 'shell/authorizations/practice';
import { CommonPracticeCrumbs } from 'practice/constants/practice-crubs';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import TableServiceMenu from './table-service-menu';
import TableUnavailabilityManagementMenu from './management-unavailability';
import UntraceableCensusManagementMenu from './management-untraceable-census';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const HomePage = (): React.ReactElement => {
  const actions = useActionAuthorization({
    ricercaPratiche: PracticeResearchAuthorizations,
    ricercaRegistro: ResearchRegisterAuthorizations,
    ricercaTipoPratica: TypePracticeAuthorizations,
    ricercaTipoOperazione: TypeOperationAuthorizations,
    ricercaResponsabile: ResponsibleAuthorizations,
    ricercaMetrica: MetricAuthorizations,
    assegnazioneOperatore: AssignOperatorPractice,
    gestioneNotifiche: [PracticeAuthorizations.GestioneNotifiche],
    aperturaResidenza: [PracticeAuthorizations.AperturaResidenza],
    aperturaImmigrazione: [PracticeAuthorizations.AperturaImmigrazione],
    aperturaAnagrafeTemporanea: [PracticeAuthorizations.AperturaAnagrafeTemporanea],
    aperturaEmigrazione: [PracticeAuthorizations.AperturaEmigrazione],
    aperturaIrreperibilita: [PracticeAuthorizations.AperturaIrreperibilita],
    stampaIrreperibilita: [PracticeAuthorizations.StampaIrreperibilita],
  });

  const [tableServiceSubmenuAnchor, setTableServiceSubmenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const isTableServiceSubmenuVisible = Boolean(tableServiceSubmenuAnchor);

  const handleTableServiceMenuClick = useEventCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setTableServiceSubmenuAnchor(event.currentTarget);
  });

  const handleTableServiceSubmenuClose = useEventCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTableServiceSubmenuAnchor(null);
  });

  const [
    tableUnavailabilityManagementSubmenuAnchor,
    setTableUnavailabilityManagementSubmenuAnchor,
  ] = useState<null | HTMLElement>(null);

  const isTableUnavailabilityManagementSubmenuVisible = Boolean(
    tableUnavailabilityManagementSubmenuAnchor
  );

  const handleTableUnavailabilityManagementMenuClick = useEventCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setTableUnavailabilityManagementSubmenuAnchor(event.currentTarget);
    }
  );

  const handleTableUnavailabilityManagementSubmenuClose = useEventCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setTableUnavailabilityManagementSubmenuAnchor(null);
    }
  );

  const [untraceableCensusManagementSubmenuAnchor, setUntraceableCensusManagementSubmenuAnchor] =
    useState<null | HTMLElement>(null);

  const isUntraceableCensusManagementSubmenuVisible = Boolean(
    untraceableCensusManagementSubmenuAnchor
  );

  const handleUntraceableCensusManagementMenuClick = useEventCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setUntraceableCensusManagementSubmenuAnchor(event.currentTarget);
    }
  );

  const handleUntraceableCensusManagementSubmenuClose = useEventCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setUntraceableCensusManagementSubmenuAnchor(null);
    }
  );

  const { userProfile } = useUserProfile();

  const gestioneTabelleSistema = userProfile.datiLogin?.profile?.feature?.sistema;

  return (
    <Fragment>
      <Breadcrumbs items={CommonPracticeCrumbs} />
      <PmiTitle title="Pratiche" paddingBottom={1} />
      <div className="pmi-mb-4">
        <Typography component="div" fontSize=".9rem" color="primary">
          {"Seleziona l'area su cui iniziare ad operare"}
        </Typography>
      </div>
      <Grid container spacing={2}>
        {!actions.ricercaPratiche ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/pratiche/ricerca" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Ricerca Pratica" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.aperturaResidenza ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/pratiche/nuova-pratica/cambio-residenza" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Apertura Pratica Cambio Residenza" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.aperturaImmigrazione ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/pratiche/nuova-pratica/immigrazione" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Apertura Pratica Immigrazione" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.aperturaEmigrazione ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/pratiche/nuova-pratica/emigrazione" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Apertura Pratica Emigrazione" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.aperturaAnagrafeTemporanea ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/pratiche/nuova-pratica/anagrafe-temporanea" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Apertura Pratica Di Anagrafe Temporanea " />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.assegnazioneOperatore ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/pratiche/assegnazione-operatore" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Assegnazione massiva operatore" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.stampaIrreperibilita && !actions.aperturaIrreperibilita ? undefined : (
          <Grid item xs={12} md={4}>
            <PmiCard>
              <CardContent>
                <TableUnavailabilityManagementMenu
                  visibility={{
                    aperturaPratica: actions.aperturaIrreperibilita,
                    stampaPratica: actions.stampaIrreperibilita,
                  }}
                  isSubmenuVisible={isTableUnavailabilityManagementSubmenuVisible}
                  onClick={handleTableUnavailabilityManagementMenuClick}
                  onClose={handleTableUnavailabilityManagementSubmenuClose}
                  submenuAnchor={tableUnavailabilityManagementSubmenuAnchor}
                />
              </CardContent>
            </PmiCard>
          </Grid>
        )}

        {(!actions.ricercaRegistro &&
          !actions.ricercaTipoPratica &&
          !actions.ricercaTipoOperazione &&
          !actions.ricercaResponsabile &&
          !actions.ricercaMetrica) ||
        gestioneTabelleSistema === '0' ? undefined : (
          <Grid item xs={12} md={4}>
            <PmiCard onClick={handleTableServiceMenuClick}>
              <CardContent>
                <TableServiceMenu
                  visibility={{
                    ricercaMetrica: actions.ricercaMetrica,
                    ricercaTipoPratica: actions.ricercaTipoPratica,
                    ricercaTipoOperazione: actions.ricercaTipoOperazione,
                    ricercaResponsabile: actions.ricercaResponsabile,
                    ricercaRegistro: actions.ricercaRegistro,
                  }}
                  isSubmenuVisible={isTableServiceSubmenuVisible}
                  onClick={handleTableServiceMenuClick}
                  onClose={handleTableServiceSubmenuClose}
                  submenuAnchor={tableServiceSubmenuAnchor}
                />
              </CardContent>
            </PmiCard>
          </Grid>
        )}

        {!actions.stampaIrreperibilita && !actions.aperturaIrreperibilita ? undefined : (
          <Grid item xs={12} md={4}>
            <PmiCard>
              <CardContent>
                <UntraceableCensusManagementMenu
                  visibility={{
                    aperturaPratica: actions.aperturaIrreperibilita,
                    stampaPratica: actions.stampaIrreperibilita,
                  }}
                  isSubmenuVisible={isUntraceableCensusManagementSubmenuVisible}
                  onClick={handleUntraceableCensusManagementMenuClick}
                  onClose={handleUntraceableCensusManagementSubmenuClose}
                  submenuAnchor={untraceableCensusManagementSubmenuAnchor}
                />
              </CardContent>
            </PmiCard>
          </Grid>
        )}
        {/* {!actions.gestioneNotifiche ? undefined : ( */}
        <Grid item xs={12} md={4}>
          <Link to="/pratiche/notifiche" className="pmi-anchor">
            <PmiCard>
              <CardContent>
                <ListItem>
                  <ListItemText primary="Gestione Notifiche" />
                </ListItem>
              </CardContent>
            </PmiCard>
          </Link>
        </Grid>
        {/* )} */}
      </Grid>
    </Fragment>
  );
};

HomePage.displayName = 'home page component';

export default HomePage;
